// Tours de terrain (décisions 4, 5, 14, 15, 21, 22, 39 et 50). Un seul tour en cours à la fois, partagé
// par l'équipe ; il couvre tous les emplacements paramétrés et reste ouvert jusqu'à ce qu'on le termine.
// Tour : { _id, name, status: 'open' | 'closed', create_user, create_user_name, create_time,
//          close_time, close_user, last_activity_time, hidden_for: [userId], kept_open: [{ user, timestamp }],
//          entries: [action] }
// Action sur un emplacement : { id, location, kind: 'inventory' | 'ok' | 'comment' | 'photo' | 'intervention' | 'reset',
//          user, user_name, timestamp, inventory_id, gaps, same, comment, photo, label, type, description }
// Une entrée sans kind (tours du lot 5) compte comme un inventaire.
// Collection `tours` (TourModel). Les créations et modifications passent par fieldApi.js (le serveur garde
// les entrées de chacun) ; les fonctions ci-dessous calculent l'état suivant d'un tour pour l'écran.

import { DAY, formatShortDate, formatDateTime } from './fieldDates'
import { normalizeLocation } from './fieldSearch'
import { isActiveComment } from './fieldNotes'
import { plural } from './fieldReference'
import { shortName } from './fieldLabels'

// Droit « Inventaires » à 7 (accès/admin 4 + écriture 2 + lecture 1) dans les permissions du dashboard.
// La ligne « Admin » des permissions, elle, sert à administrer les autres utilisateurs.
export const ADMIN_MODE = 7

export const REMIND_DAYS = 30

// « Tour d'automne 2026 », proposé à la création et modifiable (canvas W14)
export function defaultTourName(now)
{
	const date = new Date(now * 1000)
	const month = date.getMonth()
	let season = 'Tour d\'hiver'
	if (month >= 2 && month <= 4)
		season = 'Tour de printemps'
	else if (month >= 5 && month <= 7)
		season = 'Tour d\'été'
	else if (month >= 8 && month <= 10)
		season = 'Tour d\'automne'
	return season + ' ' + date.getFullYear()
}

// Un commentaire supprimé ou une action annulée ne comptent plus
export function isActiveEntry(entry)
{
	if (entry.kind === 'comment')
		return !!entry.comment && isActiveComment(entry.comment)
	return !entry.deleted_time
}

function activeEntries(tour)
{
	return ((tour && tour.entries) || []).filter(isActiveEntry)
}

// Un inventaire par emplacement et par tour (décision 15) : on compte les emplacements distincts
export function doneCount(tour)
{
	if (typeof tour.done_count === 'number' && !tour.entries)
		return tour.done_count
	// un emplacement marqué non fait ne compte plus tant qu'aucune action n'est venue après
	const counts = new Map()
	activeEntries(tour).forEach(entry => {
		const key = normalizeLocation(entry.location)
		counts.set(key, entry.kind === 'reset' ? 0 : (counts.get(key) || 0) + 1)
	})
	return Array.from(counts.values()).filter(count => count > 0).length
}

// Emplacements réellement faits, comme dans le tour : parmi les emplacements paramétrés, ceux qui ne sont plus
// « Pas encore fait » (un « non fait » ne compte plus, un emplacement retiré de la liste non plus).
// null tant que la liste des emplacements n'est pas chargée.
export function pitchDoneCount(tour, pitches)
{
	if (!tour || !pitches)
		return null
	return pitches.length - stateCounts(pitches, tour).todo
}

let sequence = 0

export function newTourAction({ location, kind, user, now, ...fields })
{
	sequence++
	return {
		id: 'action-' + now + '-' + sequence,
		location,
		kind,
		user: user ? user._id : null,
		user_name: user ? ((user.firstname || '') + ' ' + (user.lastname || '')).trim() : '',
		timestamp: now,
		...fields
	}
}

export function withActions(tour, actions, now)
{
	return { ...tour, last_activity_time: now, entries: (tour.entries || []).concat(actions) }
}

export function withoutActions(tour, ids)
{
	return { ...tour, entries: (tour.entries || []).filter(entry => !ids.includes(entry.id)) }
}

// Commentaire modifié ou supprimé (décision 31) : l'action garde sa place, son commentaire change
export function withActionComment(tour, comment)
{
	return { ...tour, entries: (tour.entries || []).map(entry => (entry.kind === 'comment' && entry.comment && entry.comment.id === comment.id ? { ...entry, comment } : entry)) }
}

// Photo modifiée (description) ou supprimée (deletedTime) : l'action garde sa place, Annuler la rétablit
export function withActionPhoto(tour, photo)
{
	return { ...tour, entries: (tour.entries || []).map(entry => (entry.kind === 'photo' && entry.photo && entry.photo.id === photo.id ? { ...entry, photo, deleted_time: photo.deletedTime || null } : entry)) }
}

export function withEntry(tour, location, inventoryId, user, now)
{
	return withActions(tour, [newTourAction({ location, kind: 'inventory', user, now, inventory_id: inventoryId })], now)
}

// Tout ce qui est enregistré sur l'emplacement (actions du tour, hors marque « non fait »), qu'il compte encore ou non
export function entriesAt(tour, location)
{
	const needle = normalizeLocation(location)
	return activeEntries(tour).filter(entry => entry.kind !== 'reset' && normalizeLocation(entry.location) === needle)
}

// « Marquer non fait » ajoute une action 'reset' : rien n'est supprimé (inventaire, interventions, photos et commentaires
// restent), mais l'emplacement repasse à « Pas encore fait » tant qu'aucune action n'est venue après la dernière marque,
// dans l'ordre d'enregistrement. Dès qu'une action revient (« RAS » par exemple), l'emplacement compte de nouveau, avec
// la couleur de tout ce qu'il contient : un inventaire déjà déposé le rend rouge, une intervention déjà créée jaune, les
// deux bicolore. Renvoie les actions qui comptent pour l'état affiché (aucune tant que l'emplacement est « non fait »).
export function currentEntriesAt(tour, location)
{
	const needle = normalizeLocation(location)
	const here = activeEntries(tour).filter(entry => normalizeLocation(entry.location) === needle)
	let start = 0
	here.forEach((entry, index) => {
		if (entry.kind === 'reset')
			start = index + 1
	})
	if (here.slice(start).filter(entry => entry.kind !== 'reset').length === 0)
		return []
	return here.filter(entry => entry.kind !== 'reset')
}

// Avancement d'un emplacement (décision 50) : un commentaire ou une photo seuls comptent comme « Fait ». Un inventaire
// déposé l'emporte sur le reste (« Fait avec inventaire »), puis une intervention (« Fait avec intervention »).
export const TOUR_STATES = {
	todo: { background: '#E2E8F0', color: '#334155', label: 'Pas encore fait', plural: 'Pas encore faits' },
	intervention: { background: '#FFB82E', color: '#000095', label: 'Fait avec intervention', plural: 'Faits avec intervention' },
	done: { background: '#34D399', color: '#0F172A', label: 'Fait', plural: 'Faits' },
	inventory: { background: '#F37F6B', color: '#0F172A', label: 'Fait avec inventaire', plural: 'Faits avec inventaire' }
}

export const STATE_ORDER = ['todo', 'done', 'intervention', 'inventory']

export function stateOfEntries(entries)
{
	if (entries.length === 0)
		return 'todo'
	if (entries.some(entry => entry.kind === 'inventory'))
		return 'inventory'
	return entries.some(entry => entry.kind === 'intervention') ? 'intervention' : 'done'
}

export function pitchState(tour, location)
{
	return stateOfEntries(currentEntriesAt(tour, location))
}

// Filtre d'avancement du plan : « Faits avec intervention » retient tout emplacement qui a au moins une
// intervention, même si un inventaire y est aussi déposé (l'état affiché reste alors « avec inventaire »)
export function pitchMatchesState(tour, location, state)
{
	if (state === 'intervention')
		return currentEntriesAt(tour, location).some(entry => entry.kind === 'intervention')
	return pitchState(tour, location) === state
}

// Comptes des filtres d'avancement du plan (pitchMatchesState) : un emplacement avec inventaire et intervention
// compte dans « avec inventaire » et dans « avec intervention »
export function filterCounts(pitches, tour)
{
	const counts = { todo: 0, done: 0, intervention: 0, inventory: 0 }
	Object.keys(counts).forEach(state => {
		counts[state] = (pitches || []).filter(pitch => pitchMatchesState(tour, pitch.location, state)).length
	})
	return counts
}

// Inventaire déposé ET au moins une intervention sur l'emplacement : le plan l'affiche en deux couleurs
export function hasInventoryAndIntervention(tour, location)
{
	const entries = currentEntriesAt(tour, location)
	return entries.some(entry => entry.kind === 'inventory') && entries.some(entry => entry.kind === 'intervention')
}

// Au moins un commentaire (non supprimé) sur l'emplacement : le plan l'annonce par une bulle dans le coin de la tuile
export function hasComment(tour, location)
{
	return entriesAt(tour, location).some(entry => entry.kind === 'comment' && !(entry.comment && entry.comment.commentDeletedTime))
}

// Zones : le champ texte `zone` de chaque emplacement (PitchModel), « Zone J » pour une lettre seule
export function zoneOf(pitch)
{
	return ((pitch && pitch.zone) || '').trim()
}

export function zoneLabel(zone)
{
	if (!zone)
		return 'Sans zone'
	return zone.length === 1 ? 'Zone ' + zone : zone
}

function compareNatural(a, b)
{
	return a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' })
}

// Zones dans l'ordre naturel, « Sans zone » à la fin ; emplacements par numéro (B2 avant B10)
export function tourZones(pitches)
{
	const byZone = {}
	;(pitches || []).forEach(pitch => {
		const zone = zoneOf(pitch)
		if (!byZone[zone])
			byZone[zone] = []
		byZone[zone].push(pitch)
	})
	return Object.keys(byZone)
		.sort((a, b) => (a === '' ? 1 : 0) - (b === '' ? 1 : 0) || compareNatural(a, b))
		.map(zone => ({ zone, label: zoneLabel(zone), pitches: byZone[zone].slice().sort((a, b) => compareNatural(a.location || '', b.location || '')) }))
}

export function orderedPitches(pitches)
{
	return [].concat(...tourZones(pitches).map(group => group.pitches))
}

export function stateCounts(pitches, tour)
{
	const counts = { todo: 0, done: 0, intervention: 0, inventory: 0 }
	;(pitches || []).forEach(pitch => {
		counts[pitchState(tour, pitch.location)]++
	})
	return counts
}

// « 61/98 » sur une pastille de zone, « 61 / 98 faits » en intertitre
export function zoneProgress(pitches, tour)
{
	const done = pitches.filter(pitch => pitchState(tour, pitch.location) !== 'todo').length
	return { done, total: pitches.length }
}

// Même recherche tolérante que la saisie d'emplacement (décision 17) : « a12 » trouve « A-12 »
export function matchesQuery(pitch, query)
{
	const needle = normalizeLocation(query)
	return !needle || normalizeLocation(pitch.location).includes(needle)
}

// Emplacement suivant : le prochain pas encore fait de la même zone, dans l'ordre des numéros,
// en reprenant au début de la zone. null : la zone est terminée.
export function nextTodo(pitches, tour, location)
{
	const needle = normalizeLocation(location)
	const current = (pitches || []).find(pitch => normalizeLocation(pitch.location) === needle)
	const group = tourZones(pitches).find(item => item.zone === zoneOf(current))
	if (!group)
		return null
	const index = group.pitches.findIndex(pitch => normalizeLocation(pitch.location) === needle)
	const after = group.pitches.slice(index + 1).concat(group.pitches.slice(0, Math.max(index, 0)))
	return after.find(pitch => normalizeLocation(pitch.location) !== needle && pitchState(tour, pitch.location) === 'todo') || null
}

// Ligne de la vue Liste : « Pas encore fait », « Fait le 22 août, 2 interventions »
export function pitchReason(tour, location, now)
{
	const entries = currentEntriesAt(tour, location)
	if (entries.length === 0)
		return 'Pas encore fait'
	const last = Math.max(...entries.map(entry => entry.timestamp || 0))
	const interventions = entries.filter(entry => entry.kind === 'intervention').length
	return 'Fait le ' + formatShortDate(last, now) + (interventions > 0 ? ', ' + plural(interventions, 'intervention') : '')
}

// « 11:05 » aujourd'hui, « 21 oct. à 11:05 » sinon
export function whenLabel(seconds, now)
{
	const date = new Date(seconds * 1000)
	const today = new Date(now * 1000)
	if (date.toDateString() === today.toDateString())
		return String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
	return formatDateTime(seconds, now)
}

function lowerFirst(text)
{
	return text ? text.charAt(0).toLowerCase() + text.slice(1) : ''
}

// Bloc « Dans ce tour » (décision 15) : qui, quand, inventaire et écarts, interventions
export function activityLines(entries, user, now)
{
	return entries
		.filter(entry => entry.kind !== 'comment' && entry.kind !== 'photo')
		.slice()
		.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
		.map(entry => {
			const mine = !!user && entry.user === user._id
			const author = shortName(entry.user_name) || (mine ? 'Vous' : 'Un collègue')
			let label = 'Inventaire du tour : ' + (entry.gaps ? plural(entry.gaps, 'écart') : 'aucun écart')
			if (entry.kind === 'ok')
				label = 'Rien à signaler'
			else if (entry.kind === 'intervention')
				label = 'Intervention ' + entry.type + ' : ' + lowerFirst(entry.description || entry.label)
			return { id: entry.id, label, meta: author + ', ' + whenLabel(entry.timestamp, now) }
		})
}

// Administrateur des inventaires : droit « Inventaires » à 7 exactement. Seul lui démarre, termine ou
// rouvre un tour (le serveur le vérifie aussi) et voit l'onglet Équipe de Mon activité.
export function isAdmin(user)
{
	return !!user && !!user.permissions && Number(user.permissions.inventories) === ADMIN_MODE
}

export function canStartTour(user)
{
	return isAdmin(user)
}

// Terminer (ou rouvrir) : un administrateur
export function canEndTour(tour, user)
{
	return isAdmin(user)
}

export function isHiddenFor(tour, user)
{
	return !!user && (tour.hidden_for || []).includes(user._id)
}

// Relance (décision 22) : 30 jours sans ajout ; « Le garder en cours » repousse de 30 nouveaux jours
export function shouldRemind(tour, user, now)
{
	if (!tour || tour.status !== 'open' || !user)
		return false
	const limit = REMIND_DAYS * DAY
	if (now - (tour.last_activity_time || tour.create_time) < limit)
		return false
	const keeps = (tour.kept_open || []).filter(item => item.user === user._id).map(item => item.timestamp)
	const lastKeep = keeps.length > 0 ? Math.max(...keeps) : 0
	return now - lastKeep >= limit
}

// « Aucun ajout depuis le 19 sept. »
export function inactivityLabel(tour, now)
{
	return 'Aucun ajout depuis le ' + formatShortDate(tour.last_activity_time || tour.create_time, now)
}

// Adresse d'un tour à partager (décision 39) : /inventaires-terrain/tour/<id>
export function tourPath(tour)
{
	return '/inventaires-terrain/tour/' + encodeURIComponent(tour._id)
}

function decodeSegment(value)
{
	try
	{
		return decodeURIComponent(value)
	}
	catch (err)
	{
		return value
	}
}

export function tourIdFromPath(pathname)
{
	const match = /\/inventaires-terrain\/tour\/([^/?#]+)/.exec(pathname || '')
	return match ? decodeSegment(match[1]) : null
}

// Adresses d'un emplacement du tour : /inventaires-terrain/tour/<id>/<emplacement> (la fiche) et, avec « /recap »,
// son compte rendu. Elles gardent l'emplacement parcouru dans l'historique : en revenant d'une intervention ouverte
// depuis le compte rendu, on retrouve ce compte rendu.
export function pitchPath(tour, location, recap = false)
{
	return tourPath(tour) + '/' + encodeURIComponent(location) + (recap ? '/recap' : '')
}

export function pitchRecapFromPath(pathname)
{
	return /\/inventaires-terrain\/tour\/[^/?#]+\/[^/?#]+\/recap\/?$/.test(pathname || '')
}

export function pitchLocationFromPath(pathname)
{
	const match = /\/inventaires-terrain\/tour\/[^/?#]+\/([^/?#]+)/.exec(pathname || '')
	return match ? decodeSegment(match[1]) : null
}

// Ligne d'état de la fiche (retour d'Alexandra du 24/10/2026) : ce qui a été enregistré, en mots.
// « Enregistré à 21:01 : 5 photos, 1 intervention »
export function actionsSummary(entries)
{
	const count = (kind) => entries.filter(entry => entry.kind === kind || (kind === 'inventory' && !entry.kind)).length
	const inventory = entries.find(entry => entry.kind === 'inventory' || !entry.kind)
	return [
		inventory ? (inventory.same ? 'inventaire identique au dernier' : 'inventaire') : null,
		count('ok') ? 'rien à signaler' : null,
		count('comment') ? plural(count('comment'), 'commentaire') : null,
		count('photo') ? plural(count('photo'), 'photo') : null,
		count('intervention') ? plural(count('intervention'), 'intervention') : null
	].filter(Boolean).join(', ')
}

function authorLabel(entry, user)
{
	const mine = !!user && entry.user === user._id
	return shortName(entry.user_name) || (mine ? 'Vous' : 'Un collègue')
}

// Bandeau d'état de la fiche, aux couleurs du plan (retour d'Alexandra du 24/10/2026) :
// { state: 'todo' | 'done' | 'intervention' | 'inventory', title, text }. visit : actions de ce passage.
export function pitchStatus(entries, visit, user, now)
{
	if (entries.length === 0)
		return { state: 'todo', title: 'Pas encore fait dans ce tour', text: null }
	const state = stateOfEntries(entries)
	const title = state === 'inventory' ? 'Fait avec inventaire' : state === 'intervention' ? 'Fait avec intervention' : 'Fait dans ce tour'
	if (visit.length > 0)
	{
		const last = Math.max(...visit.map(entry => entry.timestamp || 0))
		return { state, title, text: 'Enregistré à ' + whenLabel(last, now) + ' : ' + actionsSummary(visit) }
	}
	const latest = entries.slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0]
	return { state, title, text: authorLabel(latest, user) + ', ' + whenLabel(latest.timestamp, now) + ' : ' + actionsSummary(entries) }
}

// Compte rendu de l'emplacement dans le tour : une ligne par geste, les photos d'un même envoi groupées
export function reportLines(entries, user, now)
{
	const lines = []
	entries.slice().sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)).forEach(entry => {
		const meta = authorLabel(entry, user) + ', ' + whenLabel(entry.timestamp, now)
		const previous = lines[lines.length - 1]
		if (entry.kind === 'photo' && previous && previous.photos && previous.user === entry.user && previous.timestamp === entry.timestamp)
		{
			previous.photos++
			previous.label = plural(previous.photos, 'photo') + ' ajoutée' + (previous.photos > 1 ? 's' : '')
			return ;
		}
		let label = activityLines([entry], user, now)[0] ? activityLines([entry], user, now)[0].label : ''
		if (entry.kind === 'comment')
			label = 'Commentaire : « ' + entry.comment.comment.trim() + ' »'
		else if (entry.kind === 'photo')
			label = '1 photo ajoutée'
		lines.push({ id: entry.id, label, meta, user: entry.user, timestamp: entry.timestamp, photos: entry.kind === 'photo' ? 1 : 0 })
	})
	return lines.map(line => ({ id: line.id, label: line.label, meta: line.meta, time: line.timestamp }))
}

// « Pocket C., 21:27 » : la dernière action sur l'emplacement (compte rendu, variante A)
export function latestLabel(entries, user, now)
{
	if (entries.length === 0)
		return null
	const latest = entries.slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0]
	return authorLabel(latest, user) + ', ' + whenLabel(latest.timestamp, now)
}

// Prochain lieu suggéré (retours d'Alexandra du 24/10/2026) : c'est la personne qui choisit. On propose
// les emplacements pas encore faits qui se suivent dans la même zone : d'abord ceux qui viennent après,
// dans l'ordre des numéros, puis ceux d'avant, du plus proche au plus loin. Zone terminée : le début
// de la zone suivante qui a encore des emplacements à faire.
// → { pitches, zone, sameZone }
export function suggestNext(pitches, tour, location, limit = 6)
{
	const needle = normalizeLocation(location)
	const zones = tourZones(pitches)
	const index = zones.findIndex(group => group.pitches.some(pitch => normalizeLocation(pitch.location) === needle))
	const todo = (list) => list.filter(pitch => normalizeLocation(pitch.location) !== needle && pitchState(tour, pitch.location) === 'todo')
	if (index >= 0)
	{
		const list = zones[index].pitches
		const position = list.findIndex(pitch => normalizeLocation(pitch.location) === needle)
		const ordered = todo(list.slice(position + 1)).concat(todo(list.slice(0, position)).reverse())
		if (ordered.length > 0)
			return { pitches: ordered.slice(0, limit), zone: zones[index].zone, sameZone: true }
	}
	for (let step = 1; step <= zones.length; step++)
	{
		const group = zones[((index < 0 ? 0 : index) + step) % zones.length]
		const rest = todo(group.pitches)
		if (rest.length > 0)
			return { pitches: rest.slice(0, limit), zone: group.zone, sameZone: false }
	}
	return { pitches: [], zone: index >= 0 ? zones[index].zone : '', sameZone: true }
}
